import type { Block, SpaceEntry } from '../types.js'
import { cloneBlock, cloneSpaceEntry, isLiveBlock, isDeletionReceipt } from './spaceCodec.js'
import { generateId } from '../id.js'
import {
  createLilodataBlock,
  extractLilodataBlock,
  parseLilodataText
} from '../lilodata.js'

export type InstantiatedSeedSpace = {
  blocks: Block[]
  metaBlock?: Block
}

type SeedComparableBlock = {
  text: string
  meta: Record<string, number>
  xyzw: [number, number, number, number]
  link: string[]
}

export function instantiateSeedSpace(seedBlocks: Block[], opts?: { fallbackPaperLabel?: string }): InstantiatedSeedSpace {
  const { blocks, metaBlock } = extractLilodataBlock(seedBlocks.map(cloneBlock))
  const paperLabel = metaBlock ? parseLilodataText(metaBlock.text).paperLabel : undefined
  const nextBlocks: Block[] = blocks.map(block => ({
    ...cloneBlock(block),
    ided: [generateId(), ''] as Block['ided']
  }))

  const idMap = new Map<string, string>()
  blocks.forEach((block, index) => {
    idMap.set(block.ided[0], nextBlocks[index].ided[0])
  })

  nextBlocks.forEach((block, index) => {
    block.ided[1] = block.ided[0]
    block.link = blocks[index].link.map(targetId => idMap.get(targetId) ?? targetId)
  })

  const resolvedPaperLabel = paperLabel ?? opts?.fallbackPaperLabel
  const nextMetaBlock = resolvedPaperLabel
    ? createLilodataBlock(resolvedPaperLabel)
    : undefined

  return { blocks: nextBlocks, metaBlock: nextMetaBlock }
}

export function serializeSeedComparableBlocks(blocks: SpaceEntry[]): string {
  const { blocks: contentBlocks } = extractLilodataBlock(blocks.map(cloneSpaceEntry))
  const indexById = new Map<string, number>()
  contentBlocks.forEach((block, index) => {
    indexById.set(block.ided[0], index)
  })

  const comparable: Array<SeedComparableBlock | { text: string, ided: [string, string] }> = contentBlocks.filter(isLiveBlock).map(block => ({
    text: block.text,
    meta: block.meta ? { ...block.meta } : {},
    xyzw: [...block.xyzw] as Block['xyzw'],
    link: block.link.map(targetId => {
      const targetIndex = indexById.get(targetId)
      return targetIndex === undefined ? `external:${targetId}` : `block:${targetIndex}`
    })
  }))

  comparable.push(...contentBlocks.filter(isDeletionReceipt))
  return JSON.stringify(comparable)
}

export function serializeSeedComparableSpace(
  blocks: SpaceEntry[],
  metaBlock?: Block
): string {
  const { metaBlock: embeddedMeta } = extractLilodataBlock(blocks.map(cloneSpaceEntry))
  const passport = metaBlock ?? embeddedMeta
  const parsed = passport ? parseLilodataText(passport.text) : undefined
  return JSON.stringify({
    blocks: JSON.parse(serializeSeedComparableBlocks(blocks)),
    lilodata: {
      paper: parsed?.paperLabel ?? null,
      slug: parsed?.slug?.trim() || null
    }
  })
}
